/**
 * Approval Request API Contract
 *
 * Mirrors: backend/src/routes/approvalRequest.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateApprovalRequestOrpcInputSchema, ApprovalRequestResponseSchema, RespondToApprovalRequestInputSchema, GetApprovalRequestsOrpcQuerySchema, GetMyApprovalRequestsOrpcQuerySchema, GetMyResponsesOrpcQuerySchema, GetPendingCountsOrpcQuerySchema, ApprovalRequestCountsByTypeResponseSchema, ApprovalRequestTypeSchema, } from "@hourly/api/approvalRequest";
/**
 * POST /approval-requests
 * Create a new approval request
 * requesterId and companyId are extracted from JWT context
 */
export const createApprovalRequest = oc
    .route({
    method: "POST",
    path: "/approval-requests",
    summary: "Create a new approval request",
    tags: ["Approval Requests"],
    successStatus: 201,
})
    .input(CreateApprovalRequestOrpcInputSchema)
    .output(ApprovalRequestResponseSchema);
/**
 * GET /approval-requests/{id}
 * Get a single approval request by ID
 */
export const getApprovalRequest = oc
    .route({
    method: "GET",
    path: "/approval-requests/{id}",
    summary: "Get approval request by ID",
    tags: ["Approval Requests"],
})
    .input(z.object({ id: z.uuid() }))
    .output(ApprovalRequestResponseSchema);
/**
 * GET /approval-requests/my
 * List the current user's own approval requests
 * userId and companyId are extracted from JWT context
 */
export const getMyApprovalRequests = oc
    .route({
    method: "GET",
    path: "/approval-requests/my",
    summary: "List my approval requests",
    tags: ["Approval Requests"],
    inputStructure: "detailed",
})
    .input(z.object({
    query: GetMyApprovalRequestsOrpcQuerySchema,
}))
    .output(z.array(ApprovalRequestResponseSchema));
/**
 * GET /approval-requests/my-responses
 * List approval requests I responded to (approved/rejected)
 * responderId and companyId are extracted from JWT context
 */
export const getMyResponses = oc
    .route({
    method: "GET",
    path: "/approval-requests/my-responses",
    summary: "List requests I responded to",
    tags: ["Approval Requests"],
    inputStructure: "detailed",
})
    .input(z.object({
    query: GetMyResponsesOrpcQuerySchema,
}))
    .output(z.array(ApprovalRequestResponseSchema));
/**
 * GET /approval-requests/targeted
 * List approval requests targeted to current user (peer-to-peer)
 * userId and companyId are extracted from JWT context
 */
export const getTargetedApprovalRequests = oc
    .route({
    method: "GET",
    path: "/approval-requests/targeted",
    summary: "List approval requests targeted to current user",
    tags: ["Approval Requests"],
    inputStructure: "detailed",
})
    .input(z.object({
    query: z.object({
        requestType: ApprovalRequestTypeSchema.optional(),
    }),
}))
    .output(z.array(ApprovalRequestResponseSchema));
/**
 * GET /approval-requests
 * List approval requests with filters
 * companyId is extracted from JWT context
 */
export const getApprovalRequests = oc
    .route({
    method: "GET",
    path: "/approval-requests",
    summary: "List approval requests",
    tags: ["Approval Requests"],
    inputStructure: "detailed",
})
    .input(z.object({
    query: GetApprovalRequestsOrpcQuerySchema,
}))
    .output(z.array(ApprovalRequestResponseSchema));
/**
 * POST /approval-requests/{id}/respond
 * Approve or reject an approval request
 * Only the designated approverId can respond
 */
export const respondToApprovalRequest = oc
    .route({
    method: "POST",
    path: "/approval-requests/{id}/respond",
    summary: "Respond to approval request",
    tags: ["Approval Requests"],
})
    .input(z.object({ id: z.uuid() }).merge(RespondToApprovalRequestInputSchema))
    .output(ApprovalRequestResponseSchema);
/**
 * GET /approval-requests/counts-pending
 * Get counts of pending approval requests grouped by type
 * companyId and userId are extracted from JWT context
 */
export const getCountsPending = oc
    .route({
    method: "GET",
    path: "/approval-requests/counts-pending",
    summary: "Get pending approval request counts by type",
    tags: ["Approval Requests"],
    inputStructure: "detailed",
})
    .input(z.object({
    query: GetPendingCountsOrpcQuerySchema,
}))
    .output(ApprovalRequestCountsByTypeResponseSchema);
/**
 * DELETE /approval-requests/{id}
 * Cancel an approval request
 * Only the requester can cancel their own PENDING requests
 */
export const deleteApprovalRequest = oc
    .route({
    method: "DELETE",
    path: "/approval-requests/{id}",
    summary: "Cancel an approval request",
    tags: ["Approval Requests"],
})
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }));
// ============================================
// Contract Router
// ============================================
export const approvalRequestContract = {
    create: createApprovalRequest,
    my: getMyApprovalRequests,
    myResponses: getMyResponses,
    targeted: getTargetedApprovalRequests,
    get: getApprovalRequest,
    list: getApprovalRequests,
    respond: respondToApprovalRequest,
    countsPending: getCountsPending,
    delete: deleteApprovalRequest,
};
//# sourceMappingURL=approvalRequest.js.map